import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveSearch, setTrackQuery, setArtistQuery } from '../../redux/slices/djpalSlice';
import { searchTrack, searchArtist, searchTracksList, searchTracksPlayedByArtist, loadMoreTracksList } from '../../redux/thunk/djpalThunk';
import { addSearchHistoryEntry } from '../../redux/thunk/searchHistoryThunk';
import LikeButton from '../LikeButton';
import TrackContextMenu from '../TrackContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import DJPalAudioPreview from './DJPalAudioPreview';
import ErrorBanner from '../ErrorBanner';
import MultiArtistText from '../MultiArtistText';
import { entityId } from '../../utils/entityId';
import PlayAllButton from '../common/PlayAllButton';
import CoverArt from '../CoverArt';


const TrackListSearch = () => {
    const dispatch = useDispatch();
    const { trackListQuery, trackListResults, trackListPagination, trackListContext, isSearching, searchError, dateRange } = useSelector(state => state.djpal);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    // Right-click on a track row opens the same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();

    const recordTrackHistory = useCallback((title, artist, artworkUrl, publicId) => {
        if (!isAuthenticated || !title) return;
        const q = artist ? `${artist} - ${title}` : title;
        dispatch(addSearchHistoryEntry({
            entityType: 'track',
            entityPublicId: publicId || `q:${q}`,
            entityName: title,
            entitySubtitle: artist || null,
            entityArtwork: artworkUrl || null,
            surface: 'mixpedia',
        }));
    }, [dispatch, isAuthenticated]);








    const handleTrackClick = useCallback((track) => {
        const q = track.artist ? `${track.artist} - ${track.title}` : track.title;
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(q));
        dispatch(searchTrack({ trackName: q, trackId: entityId(track), artworkUrl: track.artworkUrl || track.artwork || null }));
        recordTrackHistory(
            track.title,
            track.artist || null,
            track.artworkUrl || track.artwork || null,
            entityId(track),
        );
    }, [dispatch, recordTrackHistory]);

    const handleArtistClick = useCallback(({ id, name, artworkUrl }) => {
        if (!name) return;
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(name));
        dispatch(searchArtist({ artistName: name, artistId: id || null, artworkUrl: artworkUrl || null }));
    }, [dispatch]);

    useEffect(() => {
        if (!trackListPagination?.hasNext) return;
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(async (entries) => {
            if (!entries[0].isIntersecting || loadingMore) return;
            setLoadingMore(true);
            await dispatch(loadMoreTracksList({
                query: trackListQuery,
                page: (trackListPagination?.page || 1) + 1,
                dateRange,
            }));
            setLoadingMore(false);
        // Wide lead: the next page's data AND its cover warm must land before
        // the user scrolls onto the new rows (see MixpediaBrowsePage).
        }, { rootMargin: '1200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [dispatch, trackListPagination, trackListQuery, loadingMore, dateRange]);

    const tracks = Array.isArray(trackListResults) ? trackListResults : [];
    // Only surface the date-range control once the user has committed to a
    // search. If a range is already active we keep it visible so the user can
    // see/clear the filter without running a new query first.

    return (
        <div className="djpal-track-list-search">
            {(trackListQuery || trackListContext) && (
                <div className="djpal-track-list-header">
                    {(() => {
                        const ctx = trackListContext;
                        const labelFor = ctx?.kind === 'playedBy'
                            ? `Tracks played by ${ctx.artistName}`
                            : ctx?.kind === 'artistTracks'
                                ? (ctx.relation === 'played'
                                    ? `Tracks played by ${ctx.artistName}`
                                    : `${ctx.artistName}'s tracks played by others`)
                                : ctx?.kind === 'frequentlyPlayedWith'
                                    ? `Frequently played with ${ctx.trackName}`
                                    : ctx?.kind === 'aiRecsFor'
                                        ? `AI Recommendations for ${ctx.trackName}`
                                        : null;
                        if (labelFor) return <h2 className="djpal-track-list-title">{labelFor}</h2>;
                        // With the in-view search bar gone, the query header is
                        // the only thing naming this list — show it everywhere.
                        if (trackListQuery) return <h2 className="djpal-track-list-title">Tracks matching "{trackListQuery}"</h2>;
                        return null;
                    })()}
                    {tracks.length > 0 && (
                        <PlayAllButton tracks={tracks} from="track_list" />
                    )}
                    {trackListPagination?.totalCount != null && (
                        <span className="djpal-track-list-count">
                            {trackListPagination.totalCount >= 500 ? '500+' : trackListPagination.totalCount} {trackListPagination.totalCount === 1 ? 'result' : 'results'}
                        </span>
                    )}
                </div>
            )}

            {searchError && (
                <ErrorBanner
                    error={searchError}
                    surface="mixpedia_search"
                    onRetry={() => {
                        if (trackListContext?.kind === 'artistTracks') {
                            dispatch(searchTracksPlayedByArtist({
                                relation: trackListContext.relation,
                                artistName: trackListContext.artistName,
                                artistPublicId: trackListContext.artistPublicId,
                                dateRange,
                            }));
                        } else if (trackListQuery) {
                            dispatch(searchTracksList({ query: trackListQuery }));
                        }
                    }}
                />
            )}

            {isSearching && tracks.length === 0 && (
                <div className="djpal-skel">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
                        <div className="djpal-skel-track-row" key={i}>
                            <div className="djpal-skel-block skel-art" />
                            <div className="skel-track-lines">
                                <div className="djpal-skel-block skel-line" />
                                <div className="djpal-skel-block skel-line short" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!isSearching && tracks.length === 0 && trackListResults != null && (
                <div className="djpal-no-results">No tracks found</div>
            )}

            {tracks.length > 0 && (
                <div className="djpal-track-list">
                    {tracks.map((t, idx) => {
                        const art = t.artworkUrl || t.artwork || '';
                        const previewId = `tracklist-${entityId(t) || idx}`;
                        return (
                            <div
                                key={entityId(t) || idx}
                                className="djpal-track-row clickable"
                                onClick={() => handleTrackClick(t)}
                                onContextMenu={openRowCtx(`tl-${entityId(t) || idx}`)}
                            >
                                <DJPalAudioPreview
                                    src={t.sampleUrl || t.previewUrl || t.preview_url || t.preview}
                                    youtubeId={t.youtubeId}
                                    soundcloudUrl={t.soundcloudUrl}
                                    trackId={t.id}
                                    durationSec={t.durationSec ?? t.duration}
                                    previewId={previewId}
                                    trackName={t.title}
                                    trackArtist={t.artist}
                                    trackArtists={t.artists}
                                    trackCover={art}
                                    playContext={{ items: tracks, index: idx, type: 'tracks' }}
                                    playbackBlocked={t.playbackBlocked}
                                >
                                    <div className="track-artwork">
                                        {art ? <CoverArt src={art} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                                    </div>
                                </DJPalAudioPreview>
                                <div className="track-info">
                                    <span className="track-label" title={t.title}>{t.title}</span>
                                    {(t.artist || (t.artists && t.artists.length > 0)) && (
                                        <span className="track-sublabel">
                                            <MultiArtistText
                                                artists={t.artists}
                                                fallbackText={t.artist}
                                                segmentClassName="clickable-artist"
                                                onSelect={handleArtistClick}
                                            />
                                        </span>
                                    )}
                                </div>
                                <LikeButton track={t} />
                                <TrackContextMenu track={t} onSearch={(e) => { e.stopPropagation(); handleTrackClick(t); }} desktopLike openAtPoint={rowCtxFor(`tl-${entityId(t) || idx}`)} />
                            </div>
                        );
                    })}
                    {trackListPagination?.hasNext && (
                        <div ref={sentinelRef} className="djpal-load-more-sentinel">
                            {loadingMore && <span className="mix-play-spinner" />}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrackListSearch;
